import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ECOSYSTEM } from "../../data";
import { EASE, fadeUp } from "../../lib/anim";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

const TiltCard = ({ item, i }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 150, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 150, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
    setHover(false);
  };

  return (
    <motion.div
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-12%" }}
      style={{ perspective: 1200 }}
      className={SPANS[i] || "lg:col-span-6"}
    >
      <motion.article
        ref={ref}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={onLeave}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        data-testid={`ecosystem-card-${item.slug}`}
        className="group relative h-full min-h-[440px] cursor-pointer border border-white/10 bg-[#0c0d0f] overflow-hidden"
      >
        <div className="absolute inset-0 overflow-hidden">
          <motion.img
            src={item.image}
            alt={item.name}
            title={`${item.name} — MGB Aviation`}
            draggable={false}
            style={{ scale: hover ? 1.08 : 1 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="h-full w-full object-cover opacity-45 group-hover:opacity-65 transition-opacity duration-700 grayscale group-hover:grayscale-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060607] via-[#060607]/70 to-[#060607]/20" />
        </div>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[linear-gradient(115deg,transparent_40%,rgba(255,255,255,0.08)_50%,transparent_60%)]" />

        <div style={{ transform: "translateZ(55px)" }} className="relative z-10 h-full flex flex-col p-8 lg:p-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="font-mono-x text-[11px] tracking-[0.3em] text-white/40">{item.index}</span>
              <span className="h-px w-6 bg-white/20" />
              <span className="eyebrow text-white/55">{item.tag}</span>
            </div>
            <ArrowUpRight
              size={22}
              className="text-white/40 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
            />
          </div>

          <div className="mt-auto">
            <h3 className="font-display text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1] font-semibold tracking-tight text-white">
              {item.name}
            </h3>
            <span className="block h-px w-10 bg-white/40 my-6 group-hover:w-20 transition-all duration-500" />
            <p className="max-w-md text-sm sm:text-base text-white/50 leading-relaxed">{item.desc}</p>

            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ecosystem-cta-${item.slug}`}
              className="mt-7 inline-flex items-center gap-2 font-mono-x text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-white transition-colors duration-300"
            >
              {item.cta}
              <ArrowUpRight size={14} className="translate-y-px" />
            </a>
          </div>
        </div>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`ecosystem-card-link-${item.slug}`}
          aria-label={`Apri ${item.name}`}
          className="absolute inset-0 z-20"
        />
      </motion.article>
    </motion.div>
  );
};

export const Ecosystem = () => {
  return (
    <section id="ecosystem" className="relative bg-[#060607] py-28 sm:py-36" data-testid="ecosystem-section">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono-x text-[11px] tracking-[0.3em] text-white/30">02</span>
          <span className="h-px w-10 bg-white/15" />
          <span className="eyebrow">Brands</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: EASE }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-white"
          >
            I nostri <span className="metallic-text">brand</span>
          </motion.h2>
          <p className="max-w-sm text-white/45 text-sm leading-relaxed">
            Ogni brand nasce con una propria identità, ma condivide gli stessi valori che ispirano
            MGB Aviation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {ECOSYSTEM.map((item, i) => (
            <TiltCard key={item.slug} item={item} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
